import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { X } from "lucide-react";
import { CallQueryParams, StrategyType } from "@/lib/types";

interface CallFiltersProps {
  filters: CallQueryParams;
  onFiltersSubmit: (filters: CallQueryParams) => void;
  strategies?: Array<{ id: string; name: string; strategy_type?: StrategyType | null }>;
}

export function CallFilters({ filters, onFiltersSubmit, strategies = [] }: CallFiltersProps) {
  const [localFilters, setLocalFilters] = useState<CallQueryParams>(filters);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key: keyof CallQueryParams, value: any) => {
    if (value === undefined || value === "" || value === "all") {
      const { [key]: _, ...rest } = localFilters;
      setLocalFilters(rest);
    } else {
      setLocalFilters({ ...localFilters, [key]: value });
    }
    setHasChanges(true);
  };

  // Helper function to format datetime for input value (just use first 16 chars)
  const formatDateTimeForInput = (dateString: string): string => {
    if (!dateString) return "";
    return dateString.slice(0, 16);
  };

  const handleSubmit = () => {
    onFiltersSubmit({ ...localFilters, offset: 0 }); // Reset to first page on filter change
    setHasChanges(false);
  };

  const handleReset = () => {
    const resetFilters = {
      limit: filters.limit,
      offset: 0,
      sort_by: filters.sort_by || "created_at",
      sort_order: filters.sort_order || "desc",
    };
    setLocalFilters(resetFilters);
    onFiltersSubmit(resetFilters);
    setHasChanges(false);
  };

  const hasActiveFilters = Object.keys(localFilters).some(
    key => !["limit", "offset", "sort_by", "sort_order"].includes(key)
  );

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Filters</h3>
        <div className="flex gap-2">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="text-sm"
            >
              <X className="h-4 w-4 mr-1" />
              Reset
            </Button>
          )}
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={!hasChanges}
            className="text-sm"
          >
            Apply Filters
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Phone Number Filter */}
        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            placeholder="Filter by phone number"
            value={localFilters.callee_phone_number || ""}
            onChange={(e) => handleFilterChange("callee_phone_number", e.target.value)}
          />
        </div>

        {/* Status Filter */}
        <div className="space-y-2">
          <Label htmlFor="status">Call Status</Label>
          <Select
            value={localFilters.call_status || "all"}
            onValueChange={(value) => handleFilterChange("call_status", value)}
          >
            <SelectTrigger id="status">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="voicemail">Voicemail</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Strategy Type Filter */}
        <div className="space-y-2">
          <Label htmlFor="strategy-type">Strategy Type</Label>
          <Select
            value={localFilters.strategy_type || "all"}
            onValueChange={(value) => handleFilterChange("strategy_type", value)}
          >
            <SelectTrigger id="strategy-type">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="consent">Consent</SelectItem>
              <SelectItem value="check_in">Check In</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Strategy Filter */}
        {strategies.length > 0 && (
          <div className="space-y-2">
            <Label htmlFor="strategy">Strategy</Label>
            <Select
              value={localFilters.strategy_id || "all"}
              onValueChange={(value) => handleFilterChange("strategy_id", value)}
            >
              <SelectTrigger id="strategy">
                <SelectValue placeholder="All strategies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All strategies</SelectItem>
                {strategies.map((strategy) => (
                  <SelectItem key={strategy.id} value={strategy.id}>
                    {strategy.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Call Started After */}
        <div className="space-y-2">
          <Label htmlFor="started-after">Started After</Label>
          <Input
            id="started-after"
            type="datetime-local"
            value={formatDateTimeForInput(localFilters.call_started_after || "")}
            onChange={(e) => handleFilterChange("call_started_after", e.target.value || undefined)}
          />
        </div>

        {/* Call Started Before */}
        <div className="space-y-2">
          <Label htmlFor="started-before">Started Before</Label>
          <Input
            id="started-before"
            type="datetime-local"
            value={formatDateTimeForInput(localFilters.call_started_before || "")}
            onChange={(e) => handleFilterChange("call_started_before", e.target.value || undefined)}
          />
        </div>

        {/* Call Ended After */}
        <div className="space-y-2">
          <Label htmlFor="ended-after">Ended After</Label>
          <Input
            id="ended-after"
            type="datetime-local"
            value={formatDateTimeForInput(localFilters.call_ended_after || "")}
            onChange={(e) => handleFilterChange("call_ended_after", e.target.value || undefined)}
          />
        </div>

        {/* Call Ended Before */}
        <div className="space-y-2">
          <Label htmlFor="ended-before">Ended Before</Label>
          <Input
            id="ended-before"
            type="datetime-local"
            value={formatDateTimeForInput(localFilters.call_ended_before || "")}
            onChange={(e) => handleFilterChange("call_ended_before", e.target.value || undefined)}
          />
        </div>

        {/* Sort Options */}
        <div className="space-y-2">
          <Label htmlFor="sort">Sort By</Label>
          <Select
            value={localFilters.sort_by || "created_at"}
            onValueChange={(value) => handleFilterChange("sort_by", value)}
          >
            <SelectTrigger id="sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at">Created Date</SelectItem>
              <SelectItem value="call_started_at">Start Date</SelectItem>
              <SelectItem value="call_ended_at">End Date</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Sort Order */}
        <div className="space-y-2">
          <Label htmlFor="order">Sort Order</Label>
          <Select
            value={localFilters.sort_order || "desc"}
            onValueChange={(value) => handleFilterChange("sort_order", value)}
          >
            <SelectTrigger id="order">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="desc">Newest First</SelectItem>
              <SelectItem value="asc">Oldest First</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}